import { useState, useEffect } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/shared/Button';
import { Sparkles, Loader2 } from 'lucide-react';

interface UIParameter {
  param_id: string;
  param_name: string;
  param_type: string;
  tier_unlock: string;
  display_label: string;
  display_order: number;
  validation_rules: string;
  tooltip_text?: string;
}

interface ParameterFormProps {
  tier: 'free' | 'basic' | 'pro' | 'designer';
  onParametersChange: (params: Record<string, any>) => void;
  onGenerate: () => void;
  isGenerating?: boolean;
}

const API_BASE_URL = 'https://fdaia-api.lancetkeith.workers.dev';

export function ParameterForm({
  tier,
  onParametersChange,
  onGenerate,
  isGenerating = false
}: ParameterFormProps) {
  const [parameters, setParameters] = useState<UIParameter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  useEffect(() => {
    fetchParameters();
  }, [tier]);

  const fetchParameters = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/fdaia/parameters?tier=${tier}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch parameters: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Sort by display_order
        const sortedParams = data.parameters.sort(
          (a: UIParameter, b: UIParameter) => a.display_order - b.display_order
        );
        setParameters(sortedParams);

        // Initialize form values with defaults
        const initialValues: Record<string, any> = {};
        sortedParams.forEach((param: UIParameter) => {
          try {
            const rules = JSON.parse(param.validation_rules || '{}');
            // Support both 'allowed_values' and 'options' from validation_rules
            const values = rules.allowed_values || rules.options || [];
            if (values.length > 0) {
              initialValues[param.param_name] = values[0];
            }
          } catch (e) {
            console.warn(`Failed to parse validation rules for ${param.param_name}`, e);
          }
        });
        setFormValues(initialValues);
        onParametersChange(initialValues);
      } else {
        throw new Error('API returned success: false');
      }
    } catch (err) {
      console.error('Error fetching parameters:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (paramName: string, value: any) => {
    const newValues = { ...formValues, [paramName]: value };
    setFormValues(newValues);
    onParametersChange(newValues);
  };

  const renderParameterInput = (param: UIParameter) => {
    let validationRules: any = {};
    try {
      validationRules = JSON.parse(param.validation_rules || '{}');
    } catch (e) {
      console.warn(`Failed to parse validation rules for ${param.param_name}`, e);
    }

    const currentValue = formValues[param.param_name];

    switch (param.param_type) {
      case 'select':
      case 'dropdown':
        // Support both 'allowed_values' and 'options' from validation_rules
        const allowedValues = validationRules.allowed_values || validationRules.options || [];
        return (
          <div key={param.param_id} className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {param.display_label}
              {param.tier_unlock !== 'free' && (
                <span className="ml-2 text-xs text-ck-purple uppercase">
                  {param.tier_unlock}
                </span>
              )}
            </label>
            <select
              value={currentValue || ''}
              onChange={(e) => handleValueChange(param.param_name, e.target.value)}
              className="w-full rounded-lg border border-ck-dark-border bg-ck-dark-card px-3 py-2 text-sm text-foreground focus:border-ck-cyan focus:outline-none focus:ring-1 focus:ring-ck-cyan"
            >
              {allowedValues.map((value: string) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            {param.tooltip_text && (
              <p className="text-xs text-muted-foreground">{param.tooltip_text}</p>
            )}
          </div>
        );

      case 'color_picker':
        // Support both 'allowed_values' and 'options' from validation_rules
        const colorOptions = validationRules.allowed_values || validationRules.options || [];
        return (
          <div key={param.param_id} className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {param.display_label}
              {param.tier_unlock !== 'free' && (
                <span className="ml-2 text-xs text-ck-purple uppercase">
                  {param.tier_unlock}
                </span>
              )}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {colorOptions.map((color: string) => (
                <button
                  key={color}
                  onClick={() => handleValueChange(param.param_name, color)}
                  className={cn(
                    "rounded-lg border-2 px-3 py-2 text-xs font-medium transition-all",
                    currentValue === color
                      ? "border-ck-cyan bg-ck-cyan/10 text-ck-cyan"
                      : "border-ck-dark-border bg-ck-dark-card text-foreground hover:border-ck-cyan/50"
                  )}
                >
                  {color}
                </button>
              ))}
            </div>
            {param.tooltip_text && (
              <p className="text-xs text-muted-foreground">{param.tooltip_text}</p>
            )}
          </div>
        );

      case 'range':
      case 'slider':
        const min = validationRules.min || 0;
        const max = validationRules.max || 100;
        const step = validationRules.step || 1;
        return (
          <div key={param.param_id} className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {param.display_label}
              {param.tier_unlock !== 'free' && (
                <span className="ml-2 text-xs text-ck-purple uppercase">
                  {param.tier_unlock}
                </span>
              )}
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={currentValue || min}
                onChange={(e) => handleValueChange(param.param_name, e.target.value)}
                className="flex-1"
              />
              <span className="text-sm text-ck-cyan font-medium w-12 text-right">
                {currentValue || min}
              </span>
            </div>
            {param.tooltip_text && (
              <p className="text-xs text-muted-foreground">{param.tooltip_text}</p>
            )}
          </div>
        );

      case 'text':
      case 'input':
        return (
          <div key={param.param_id} className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {param.display_label}
              {param.tier_unlock !== 'free' && (
                <span className="ml-2 text-xs text-ck-purple uppercase">
                  {param.tier_unlock}
                </span>
              )}
            </label>
            <input
              type="text"
              value={currentValue || ''}
              onChange={(e) => handleValueChange(param.param_name, e.target.value)}
              className="w-full rounded-lg border border-ck-dark-border bg-ck-dark-card px-3 py-2 text-sm text-foreground focus:border-ck-cyan focus:outline-none focus:ring-1 focus:ring-ck-cyan"
              placeholder={param.tooltip_text || ''}
            />
          </div>
        );

      default:
        return (
          <div key={param.param_id} className="text-xs text-muted-foreground">
            Unsupported parameter type: {param.param_type}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-ck-cyan" />
        <span className="ml-2 text-sm text-muted-foreground">Loading parameters...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
        <p className="text-sm text-red-400">Error loading parameters: {error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchParameters}
          className="mt-3"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {parameters.map(renderParameterInput)}
      </div>

      <Button
        variant="gradient"
        size="lg"
        onClick={onGenerate}
        disabled={isGenerating}
        className="w-full"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            Generate Variant
          </>
        )}
      </Button>

      <div className="text-center text-xs text-muted-foreground">
        Current tier: <span className="font-medium text-ck-cyan uppercase">{tier}</span>
      </div>
    </div>
  );
}
